import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { languageContent } from '../utils/languageContent';
import { getSuperAdminStatus } from '../utils/adminUtils';
import { speakText } from '../utils/audioUtils';
import BottomNav from '../components/BottomNav';
import VideoPlayer from '../components/VideoPlayer';
import LessonBannerAd from '../components/LessonBannerAd';
import { showInterstitialAd } from '../utils/adMobService';

interface CourseLevel {
  level_code: string;
}

interface Chapter {
  id: string;
  name: string;
  CourseLevels: CourseLevel;
}

interface Lesson {
  id: string;
  chapter_id: string;
  title: string;
  content: string;
  content_italian?: string;
  order_index: number;
  Chapters: Chapter;
  // Video fields
  video_url?: string;
  video_duration?: number;
  video_thumbnail?: string;
  video_description?: string;
  video_title?: string;
}

const LessonScreen: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const content = languageContent[currentLanguage];
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    if (lessonId) {
      initializeLesson();
    }
  }, [lessonId]);

  const initializeLesson = async () => {
    const superAdmin = await getSuperAdminStatus();
    setIsSuperAdmin(superAdmin);
    await loadLesson(lessonId!, superAdmin);
  };

  const checkSuperAdminStatus = async () => {
    const superAdmin = await getSuperAdminStatus();
    setIsSuperAdmin(superAdmin);
  };

  useEffect(() => {
    if (lesson) {
      checkVideoProgress();
    }
  }, [lesson]);

  const loadLesson = async (lessonId: string, superAdminStatus?: boolean) => {
    try {
      const isSuperAdminUser = superAdminStatus !== undefined ? superAdminStatus : isSuperAdmin;
      let query = supabase
        .from('Lessons')
        .select(`
          *,
          Chapters!inner(
            id,
            name,
            level_id,
            CourseLevels(level_code)
          )
        `)
        .eq('id', lessonId);
      
      // Only filter by status for non-superadmins
      if (!isSuperAdminUser) {
        query = query.eq('status', 'published');
      }
      
      const { data, error } = await query.single();

      if (error) {
        console.error('Error loading lesson:', error);
        setError('Lesson not found. Redirecting...');
        setTimeout(() => navigate('/levels'), 2000);
      } else {
        console.log('Lesson loaded:', data);
        console.log('Video URL:', data.video_url);
        console.log('Has video URL:', !!data.video_url);
        setLesson(data);
      }
    } catch (error) {
      console.error('Error loading lesson:', error);
      setError('Error loading lesson. Redirecting...');
      setTimeout(() => navigate('/levels'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = () => {
    if (lessonId) {
      showInterstitialAd(); // Ad-supported break point — lesson content finished, moving to its quiz
      navigate(`/quiz/${lessonId}`, {
        state: { fromLesson: true }
      });
    }
  };

  const handleVideoComplete = () => {
    setVideoWatched(true);
    setShowVideoPlayer(false);
  };

  const handleCloseVideo = () => {
    setShowVideoPlayer(false);
  };

  const handleSkipVideo = () => {
    setVideoWatched(true);
    setShowVideoPlayer(false);
  };

  const checkVideoProgress = async () => {
    if (!lesson || !lesson.video_url) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('VideoProgress')
        .select('completed')
        .eq('user_id', user.id)
        .eq('lesson_id', lesson.id)
        .single();

      if (data?.completed) {
        setVideoWatched(true);
      }
    } catch (error) {
      console.error('Error checking video progress:', error);
    }
  };

  // Function to detect and make Italian words/phrases clickable
  const makeItalianWordsClickable = (text: string) => {
    if (!text) return null;
    
    // Regular expression to match Italian/English text (including single letters)
    const regex = /([a-zA-ZÀ-ÿ]+(?:\s+[a-zA-ZÀ-ÿ]+)*)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      // Add non-Italian text before this match
      if (match.index > lastIndex) {
        parts.push({
          text: text.slice(lastIndex, match.index),
          isItalian: false,
          isSingleLetter: false,
          key: `text-${lastIndex}`
        });
      }
      
      // Clean the phrase
      const cleanPhrase = match[0].trim();
      
      // Check if it's a single letter
      const isSingleLetter = cleanPhrase.length === 1;
      
      parts.push({
        text: cleanPhrase,
        isItalian: true,
        isSingleLetter: isSingleLetter,
        key: `italian-${match.index}`
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining non-Italian text
    if (lastIndex < text.length) {
      parts.push({
        text: text.slice(lastIndex),
        isItalian: false,
        isSingleLetter: false,
        key: `text-${lastIndex}`
      });
    }
    
    return parts.map((part) => {
      if (part.isItalian) {
        const handleClick = () => {
          console.log('👆 CLICKED ON WORD:', part.text.trim());
          console.log('👆 Is single letter?', part.isSingleLetter);
          // Don't speak single letters - they sound weird
          if (!part.isSingleLetter) {
            console.log('✅ Calling speakText for:', part.text.trim());
            speakText(part.text.trim(), 'answer');
          } else {
            console.log('⏭️ Skipping single letter');
          }
        };
        
        return (
          <span
            key={part.key}
            className="clickable-word"
            onClick={handleClick}
            onKeyPress={(e) => e.key === 'Enter' && handleClick()}
            role="button"
            tabIndex={0}
            aria-label={`Hear pronunciation of ${part.text}`}
            title="Click to hear pronunciation"
          >
            {part.text}
          </span>
        );
      }
      return <span key={part.key}>{part.text}</span>;
    });
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">
          <img src="logo512.png" alt="Shikhun Italian" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-screen">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2>{error}</h2>
          <p>Please wait while we redirect you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lesson-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => {
            // If we came from quiz, go back to lessons list (chapter page)
            // Otherwise use normal back navigation
            if (location.state?.fromQuiz && lesson?.chapter_id) {
              navigate(`/chapter/${lesson.chapter_id}/lessons`);
            } else {
              navigate(-1);
            }
          }}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content.lesson}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      <div className="lesson-content">
        <div className="lesson-header-section">
          <div className="lesson-badge">
            {lesson?.Chapters?.CourseLevels?.level_code || 'Lesson'}
          </div>
          <h2 className="lesson-title">{makeItalianWordsClickable(lesson?.title || 'Lesson Title')}</h2>
        </div>
        
        {/* Video Player Section - Full Width */}
        {lesson?.video_url ? (
          <div className="lesson-video-full">
            <VideoPlayer
              videoUrl={lesson.video_url}
              videoTitle={lesson.video_title || lesson.title}
              videoDescription={lesson.video_description}
              videoDuration={lesson.video_duration}
              chapterId={lesson.id}
              onVideoComplete={handleVideoComplete}
              autoPlay={false}
            />
          </div>
        ) : (
          <div className="lesson-no-video">
            <div className="no-video-message">
              <div className="no-video-icon">📚</div>
              <p>This lesson doesn't have a video yet.</p>
            </div>
          </div>
        )}
        
        {/* Lesson Description */}
        <div className="lesson-description-section">
          {lesson?.content ? (
            <p className="lesson-description-text">{makeItalianWordsClickable(lesson.content)}</p>
          ) : (
            <p className="lesson-description-text">
              Welcome to this lesson! In this section, you'll learn essential Italian phrases and grammar.
            </p>
          )}
          
          {lesson?.content_italian && (
            <div className="lesson-example">
              <h4>📝 Italian Example:</h4>
              <div className="example-card">
                <div className="italian-text">{makeItalianWordsClickable(lesson.content_italian)}</div>
              </div>
            </div>
          )}
        </div>


        <div className="lesson-actions">
          <button 
            className="btn-primary btn-large"
            onClick={handleStartQuiz}
          >
            <span>Continue to Quiz</span>
            <span className="arrow">→</span>
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default LessonScreen;
